/* TIỆN ÍCH GIAO DIỆN dùng chung */
import React from 'react';
import { Descriptions, Table } from 'antd';
import type { TableColumnType, TableColumnsType } from 'antd';
import type { KV } from './Types';

export const cx = (...a: (string | false | null | undefined)[]) => a.filter(Boolean).join(' ');
export const fmt = (s: number) => {
  const a = Math.abs(s);
  return `${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
};
export const money = (v: number | null) => (v == null ? '—' : `${v.toLocaleString('vi-VN')} đ`);
export const sColor = (s: string) => {
  if (s.startsWith('S4') || s.startsWith('S6') || s === 'CS1' || s === 'CS2') return '#1F4E79';
  if (s === 'S3' || s === 'S5' || s === 'CS4') return '#2E75B6';
  if (s === 'S2' || s === 'CS3') return '#4091AA';
  return '#7DB4C7';
};

export const TXT_LATE = 'font-bold text-[#B03A2E] dark:text-red-400';
export const TXT_OK = 'font-bold text-[#2E7D4F] dark:text-emerald-400';
export const TXT_NAVY = 'text-[#1F4E79] dark:text-sky-300';
export const TXT_MUTED = 'text-slate-500 dark:text-slate-400';
export const BOX = 'rounded-md border border-[#D5E2EA] bg-white dark:border-slate-700 dark:bg-slate-800';
export const Q_RING = 'ring-2 ring-inset ring-[#E07B00]';

/** Badge cam [?] = giá trị Khơ Thị chưa chốt (cấu hình được) */
export const Q: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <span className="ml-1 inline-block rounded bg-[#E07B00] px-1.5 align-middle text-xs font-bold text-white">
    [?]{children ? <> {children}</> : null}
  </span>
);

/** Văn bản có đánh dấu nhẹ: **đậm**, [[link]], !!đỏ!!, [?] */
export const Rich: React.FC<{ text: string }> = ({ text }) => (
  <div className='dark:text-white'>
    {text.split(/(\*\*.+?\*\*|\[\[.+?\]\]|!!.+?!!|\[\?\])/g).map((p, i) => {
      if (p.startsWith('**')) return <b key={i}>{p.slice(2, -2)}</b>;
      if (p.startsWith('[[')) return <span key={i} className="cursor-pointer text-[#2E75B6] underline dark:text-sky-400">{p.slice(2, -2)}</span>;
      if (p.startsWith('!!')) return <b key={i} className={TXT_LATE}>{p.slice(2, -2)}</b>;
      if (p === '[?]') return <Q key={i} />;
      return <React.Fragment key={i}>{p}</React.Fragment>;
    })}
  </div>
);

export const SChip: React.FC<{ s: string }> = ({ s }) => (
  <span className="inline-block rounded-full px-2 text-xs font-bold text-white" style={{ background: sColor(s) }}>{s}</span>
);
export const StatusChip: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <span className="inline-block rounded-full px-2 text-xs font-bold text-white" style={{ background: color }}>{children}</span>
);

export const SecTitle: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cx('mb-2 text-sm font-bold', TXT_NAVY, className)}>{children}</div>
);
export const Note: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <p className={cx('my-1.5 text-xs leading-relaxed', TXT_MUTED, className)}>{children}</p>
);
export const Card: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cx(BOX, 'p-3', className)}>{children}</div>
);
export const Demo: React.FC = () => (
  <div className="mb-3 border border-dashed border-[#E07B00] bg-[#FFF4E5] p-1.5 text-center text-xs font-bold tracking-wide text-[#8A4B00] dark:bg-amber-950/40 dark:text-amber-300 d-none">
    DỮ LIỆU MINH HOẠ — tên khách, mã nhân viên và mọi con số là giả
  </div>
);
export const Cap: React.FC<{ title: React.ReactNode; items: React.ReactNode[] }> = ({ title, items }) => (
  <div className="mb-2 flex flex-wrap items-center gap-x-5 gap-y-1 border-l-[6px] border-[#1F4E79] bg-white px-3 py-2 text-xs dark:border-sky-500 dark:bg-slate-800">
    <b className={cx('text-sm', TXT_NAVY)}>{title}</b>
    {items.map((it, i) => <span className='dark:text-white' key={i}>{it}</span>)}
  </div>
);
export const Kpi: React.FC<{ label: React.ReactNode; value: React.ReactNode; sub: React.ReactNode; late?: boolean; q?: boolean }> = ({ label, value, sub, late, q }) => (
  <div className={cx(BOX, 'p-3', q && Q_RING)}>
    <div className="text-xs text-slate-600 dark:text-slate-300">{label}</div>
    <div className={cx('my-1 text-2xl font-bold', late ? 'text-[#B03A2E] dark:text-red-400' : TXT_NAVY)}>{value}</div>
    <div className={cx('text-xs', TXT_MUTED)}>{sub}</div>
  </div>
);
export const Hint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="my-2 border-l-4 border-[#C77700] bg-[#FFF8E1] px-2.5 py-1.5 text-[12.5px] leading-relaxed dark:bg-amber-950/30">{children}</div>
);
export const Lock: React.FC<{ children: React.ReactNode; navy?: boolean }> = ({ children, navy }) => (
  <div className={cx('text-xs', navy ? TXT_NAVY : 'text-[#B03A2E] dark:text-red-400')}>{children}</div>
);

/* ---- Bảng antd dùng chung ---- */
export type WCol<T> = TableColumnType<T> & { w?: boolean };
export function DT<T extends object>(p: { columns: WCol<T>[]; data: T[]; dock?: boolean; empty?: string; rowCls?: (r: T, i: number) => string }) {
  const cols = p.columns.filter((c) => !(p.dock && c.w)) as TableColumnsType<T>;
  return (
    <>
      <style>{`
        .dt-row-striped > td {
          background-color: #EEF5F8 !important;
        }

        .dark .dt-row-striped > td {
          background-color: rgb(30 41 59 / 60%) !important;
        }
      `}</style>
      <Table<T>
        size="small"
        pagination={false}
        columns={cols}
        dataSource={p.data}
        rowKey={(_r, i) => String(i)}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: p.empty ?? '—' }}
        rowClassName={(r, i) =>
          cx(
            i % 2 ? 'dt-row-striped' : '',
            p.rowCls?.(r, i)
          )
        }
      />
    </>
  );
}
/** Bảng nhỏ từ mảng chuỗi (dùng trong ngăn hồ sơ) */
export const MiniTable: React.FC<{ head: string[]; rows: string[][]; empty: string; nowrap?: number[] }> = ({ head, rows, empty, nowrap = [] }) => {
  if (!rows.length) return <Note>{empty}</Note>;
  const columns: WCol<Record<string, string>>[] = head.map((h, i) => ({
    title: h,
    dataIndex: String(i),
    render: (v: string) => (nowrap.includes(i) ? <span className="whitespace-nowrap"><Rich text={v} /></span> : <Rich text={v} />),
  }));
  const data = rows.map((r) => Object.fromEntries(r.map((c, i) => [String(i), c])));
  return <DT columns={columns} data={data} />;
};
export const KVList: React.FC<{ items: KV }> = ({ items }) => (
  <Descriptions
    bordered
    size="small"
    column={1}
    labelStyle={{ width: 170 }}
    items={items.map(([k, v]) => ({ key: k, label: k, children: <Rich text={v} /> }))}
    className="dark:[&_.ant-descriptions-item-label]:!text-white dark:[&_.ant-descriptions-item-content]:!text-white"
  />
);
export const Link: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <span role="link" tabIndex={0} onClick={onClick} onKeyDown={(e) => e.key === 'Enter' && onClick()} className="cursor-pointer text-[#2E75B6] underline dark:text-sky-400">{children}</span>
);
export const Chips: React.FC<{ list: string[]; cur: string }> = ({ list, cur }) => (
  <span className="inline-flex flex-wrap items-center gap-1">
    {list.map((x, i) => (
      <React.Fragment key={x}>
        {i > 0 && <span className="text-slate-400">→</span>}
        <span className={cx('rounded-full px-2 text-xs', x === cur ? 'bg-[#1F4E79] font-bold text-white' : 'bg-[#EEF5F8] text-[#1F4E79] dark:bg-slate-700 dark:text-slate-200')}>{x}</span>
      </React.Fragment>
    ))}
  </span>
);
export const PhotoBox: React.FC<{ ma: string; label: string; title?: string; size?: 'sm' | 'md'; empty?: boolean; abnormal?: boolean }> = ({ ma, label, title, size = 'md', empty, abnormal }) => (
  <div
    title={title}
    className={cx(
      'relative shrink-0 rounded-sm border',
      size === 'sm' ? 'h-14 w-[74px]' : 'h-[78px] w-[104px]',
      empty
        ? 'flex items-center justify-center border-dashed border-[#9FBFD0] bg-white text-xs text-slate-500 dark:border-slate-600 dark:bg-slate-800'
        : 'border-[#B8C7D1] bg-[repeating-linear-gradient(45deg,#D9E2E8_0_6px,#E8EEF2_6px_12px)] dark:border-slate-600 dark:bg-[repeating-linear-gradient(45deg,#334155_0_6px,#3b4a5f_6px_12px)]',
      abnormal && 'border-2 border-[#B03A2E]',
    )}
  >
    {empty ? label : (
      <>
        <span className="absolute right-0.5 top-0.5 text-[11px] text-slate-500 dark:text-slate-300">{ma}</span>
        <span className="absolute bottom-0.5 left-0.5 rounded-sm bg-[#1F4E79] px-1 text-[11px] text-white">{label}</span>
      </>
    )}
  </div>
);
export const QR: React.FC<{ seed: number }> = ({ seed }) => {
  const n = 25;
  let x = seed;
  const cells: React.ReactNode[] = [];
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      if ((i < 8 && j < 8) || (i < 8 && j > n - 9) || (i > n - 9 && j < 8)) continue;
      x = (x * 1103515245 + 12345) & 0x7fffffff;
      if (x % 3 === 0) cells.push(<rect key={`${i}-${j}`} x={j} y={i} width={1} height={1} fill="#1F4E79" />);
    }
  }
  const fin = (a: number, b: number) => (
    <g key={`f${a}${b}`}>
      <rect x={a} y={b} width={7} height={7} fill="#1F4E79" />
      <rect x={a + 1} y={b + 1} width={5} height={5} fill="#fff" />
      <rect x={a + 2} y={b + 2} width={3} height={3} fill="#1F4E79" />
    </g>
  );
  return (
    <svg viewBox={`0 0 ${n} ${n}`} width={132} height={132} shapeRendering="crispEdges" className="shrink-0 border border-[#9FBFD0] bg-white" aria-label="Mã QR cọc (minh hoạ)">
      {cells}{fin(0, 0)}{fin(n - 7, 0)}{fin(0, n - 7)}
    </svg>
  );
};